import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import React, { useCallback, useMemo, useRef, useState } from 'react';
import { X, UploadCloud } from 'lucide-react';
import { parseGeoFile, extractLineStrings, extractPolygons, extractPoints, ACCEPT_FILE_EXTENSIONS, } from '../../utils/geoFileParser';
// Permissive decimal-degree parser. One placemark per line. Accepts
// commas or whitespace as separators; an optional trailing field after
// the second separator is treated as the placemark name. Lines that
// are empty or start with "#" are ignored so users can keep comments.
const parsePastedCoords = (text) => {
    const coords = [];
    const errors = [];
    const lines = text.split(/\r?\n/);
    lines.forEach((raw, idx) => {
        var _a;
        const line = raw.trim();
        if (!line || line.startsWith('#'))
            return;
        const match = line.match(/^(-?\d+(?:\.\d+)?)\s*[,\s]\s*(-?\d+(?:\.\d+)?)(?:\s*[,\s]\s*(.+))?$/);
        if (!match) {
            errors.push(`Line ${idx + 1}: couldn't read "${line}" as "lat, lng".`);
            return;
        }
        const lat = parseFloat(match[1]);
        const lng = parseFloat(match[2]);
        if (Math.abs(lat) > 90) {
            errors.push(`Line ${idx + 1}: latitude ${lat} is out of range (-90 to 90).`);
            return;
        }
        if (Math.abs(lng) > 180) {
            errors.push(`Line ${idx + 1}: longitude ${lng} is out of range (-180 to 180).`);
            return;
        }
        const name = (_a = match[3]) === null || _a === void 0 ? void 0 : _a.trim();
        coords.push(name ? { lat, lng, name } : { lat, lng });
    });
    return { coords, errors };
};
const COPY_BY_TOOL = {
    path: {
        title: 'Upload Paths',
        description: 'Import paths from KML, KMZ, GPX, or GeoJSON. Each line (KML LineString, GPX track or route, GeoJSON LineString) becomes a separate path drawing.',
        acceptedLabel: 'Accepted: .kml, .kmz, .gpx, .geojson',
        featureNoun: 'path',
        countFeatures: (g) => extractLineStrings(g).length,
    },
    polygon: {
        title: 'Upload Polygons',
        description: 'Import polygons from KML, KMZ, GeoJSON, or a zipped Shapefile (.zip containing .shp/.shx/.dbf). Each polygon in the file becomes a separate drawing.',
        acceptedLabel: 'Accepted: .kml, .kmz, .geojson, .zip (Shapefile)',
        featureNoun: 'polygon',
        countFeatures: (g) => extractPolygons(g).length,
    },
    placemark: {
        title: 'Upload Placemarks',
        description: 'Import placemarks from KML, KMZ, or GeoJSON. Each point in the file becomes a separate placemark.',
        acceptedLabel: 'Accepted: .kml, .kmz, .geojson',
        featureNoun: 'placemark',
        countFeatures: (g) => extractPoints(g).length,
    },
};
export const MarkupUploadModal = ({ tool, isOpen, onClose, onUpload, onPasteCoordinates }) => {
    const copy = COPY_BY_TOOL[tool];
    const pasteEnabled = tool === 'placemark' && !!onPasteCoordinates;
    const [mode, setMode] = useState('file');
    const [selectedFile, setSelectedFile] = useState(null);
    const [error, setError] = useState(null);
    const [isParsing, setIsParsing] = useState(false);
    const [featureCount, setFeatureCount] = useState(null);
    const [pasteText, setPasteText] = useState('');
    const inputRef = useRef(null);
    const pasteResult = useMemo(() => (pasteText.trim() ? parsePastedCoords(pasteText) : { coords: [], errors: [] }), [pasteText]);
    const reset = useCallback(() => {
        setSelectedFile(null);
        setError(null);
        setIsParsing(false);
        setFeatureCount(null);
        setPasteText('');
        setMode('file');
    }, []);
    const handleFiles = useCallback(async (files) => {
        if (!files || files.length === 0)
            return;
        const file = files[0];
        setError(null);
        setSelectedFile(file);
        setFeatureCount(null);
        setIsParsing(true);
        try {
            const { geojson, error: parseError } = await parseGeoFile(file);
            if (parseError || !geojson) {
                setError(parseError || 'Failed to parse file.');
                return;
            }
            const count = copy.countFeatures(geojson);
            if (count === 0) {
                setError(`No ${copy.featureNoun}s found in this file. Make sure the file contains ${copy.featureNoun} geometry.`);
                return;
            }
            setFeatureCount(count);
        }
        catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to parse file.');
        }
        finally {
            setIsParsing(false);
        }
    }, [copy]);
    const onBrowseClick = useCallback(() => {
        var _a;
        (_a = inputRef.current) === null || _a === void 0 ? void 0 : _a.click();
    }, []);
    const onDrop = useCallback((e) => {
        e.preventDefault();
        handleFiles(e.dataTransfer.files);
    }, [handleFiles]);
    const onDragOver = useCallback((e) => {
        e.preventDefault();
    }, []);
    const onConfirm = useCallback(() => {
        if (mode === 'paste') {
            if (pasteResult.coords.length === 0 || !onPasteCoordinates)
                return;
            onPasteCoordinates(pasteResult.coords);
            reset();
            onClose();
            return;
        }
        if (selectedFile && featureCount && featureCount > 0) {
            onUpload(selectedFile);
            reset();
            onClose();
        }
    }, [mode, pasteResult, onPasteCoordinates, selectedFile, featureCount, onUpload, onClose, reset]);
    const handleClose = useCallback(() => {
        reset();
        onClose();
    }, [reset, onClose]);
    if (!isOpen)
        return null;
    return (_jsxs("div", { className: "fixed inset-0 z-[70] flex items-center justify-center p-4", children: [_jsx("div", { className: "ls-modal-backdrop", onClick: handleClose }), _jsxs("div", { className: "ls-modal-card ls-modal-card-animate relative w-full max-w-2xl flex flex-col", style: { maxHeight: '92vh' }, children: [_jsxs("div", { className: "flex items-center justify-between p-6 ls-modal-band-top shrink-0", style: { backgroundColor: 'var(--forest)' }, children: [_jsx("h2", { className: "text-xl font-semibold", style: { color: 'var(--sand)' }, children: copy.title }), _jsx("button", { onClick: handleClose, className: "p-1 rounded transition-colors", style: { color: 'var(--sand)' }, onMouseEnter: (e) => { e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.15)'; }, onMouseLeave: (e) => { e.currentTarget.style.backgroundColor = 'transparent'; }, "aria-label": "Close", children: _jsx(X, { className: "w-5 h-5 sm:w-6 sm:h-6" }) })] }), _jsxs("div", { className: "p-6 space-y-4 overflow-y-auto min-h-0 flex-1", style: { backgroundColor: 'var(--sand)', color: 'var(--charcoal)' }, children: [pasteEnabled && (_jsx("div", { className: "flex gap-1", style: { borderBottom: '1px solid var(--pewter)' }, children: [
                                    { id: 'file', label: 'Upload file' },
                                    { id: 'paste', label: 'Paste coordinates' },
                                ].map((tab) => (_jsx("button", { onClick: () => setMode(tab.id), className: "px-4 py-2 text-sm font-medium transition-colors", style: {
                                        backgroundColor: 'transparent',
                                        color: mode === tab.id ? 'var(--forest)' : 'var(--pewter)',
                                        borderBottom: mode === tab.id ? '2px solid var(--forest)' : '2px solid transparent',
                                        marginBottom: -1,
                                    }, children: tab.label }, tab.id))) })), mode === 'file' && (_jsxs(_Fragment, { children: [_jsx("p", { className: "text-sm", style: { color: 'var(--charcoal)' }, children: copy.description }), _jsxs("div", { onDrop: onDrop, onDragOver: onDragOver, className: "flex flex-col items-center justify-center gap-3 border-2 border-dashed rounded-lg p-8 cursor-pointer", style: { borderColor: 'var(--pewter)', backgroundColor: 'white' }, onClick: onBrowseClick, children: [_jsx(UploadCloud, { className: "w-8 h-8", style: { color: 'var(--forest)' } }), _jsx("div", { className: "text-sm", style: { color: 'var(--charcoal)' }, children: "Drag and drop your file here, or click to browse" }), _jsx("div", { className: "text-xs", style: { color: 'var(--pewter)' }, children: copy.acceptedLabel }), _jsx("input", { ref: inputRef, type: "file", accept: ACCEPT_FILE_EXTENSIONS.join(','), className: "hidden", onChange: (e) => handleFiles(e.target.files) })] }), selectedFile && (_jsxs("div", { className: "text-sm", style: { color: 'var(--charcoal)' }, children: ["Selected: ", _jsx("span", { className: "font-medium", children: selectedFile.name }), isParsing && _jsx("span", { className: "ml-2 opacity-75", children: "Parsing\u2026" }), !isParsing && featureCount !== null && (_jsxs("span", { className: "ml-2", style: { color: 'var(--forest)' }, children: ["\u00B7 ", featureCount, " ", copy.featureNoun, featureCount === 1 ? '' : 's', " found"] }))] })), error && (_jsx("div", { className: "text-sm p-3 rounded-lg", style: { backgroundColor: '#fef2f2', color: '#dc2626', border: '1px solid #fecaca' }, children: error }))] })), mode === 'paste' && (_jsxs(_Fragment, { children: [_jsxs("p", { className: "text-sm", style: { color: 'var(--charcoal)' }, children: ["One placemark per line. Use decimal degrees: ", _jsx("code", { children: "lat, lng" }), " (or ", _jsx("code", { children: "lat lng" }), "). Optional trailing column becomes the placemark name. Lines starting with ", _jsx("code", { children: "#" }), " are ignored."] }), _jsx("textarea", { value: pasteText, onChange: (e) => setPasteText(e.target.value), placeholder: '49.1234, -97.6543\n49.1300, -97.6500, Well #1\n# any line starting with # is a comment', rows: 8, className: "w-full px-3 py-2 rounded-lg text-sm font-mono", style: {
                                            backgroundColor: 'white',
                                            color: 'var(--charcoal)',
                                            border: '1px solid var(--pewter)',
                                            resize: 'vertical',
                                        } }), pasteText.trim() && (_jsxs("div", { className: "text-sm", style: { color: 'var(--charcoal)' }, children: [pasteResult.coords.length > 0 && (_jsxs("span", { style: { color: 'var(--forest)' }, children: [pasteResult.coords.length, " placemark", pasteResult.coords.length === 1 ? '' : 's', " ready"] })), pasteResult.coords.length > 0 && pasteResult.errors.length > 0 && _jsx("span", { children: " \u00B7 " }), pasteResult.errors.length > 0 && (_jsxs("span", { style: { color: '#dc2626' }, children: [pasteResult.errors.length, " line", pasteResult.errors.length === 1 ? '' : 's', " skipped"] }))] })), pasteResult.errors.length > 0 && (_jsxs("div", { className: "text-xs p-3 rounded-lg space-y-1", style: { backgroundColor: '#fef2f2', color: '#dc2626', border: '1px solid #fecaca', maxHeight: 120, overflowY: 'auto' }, children: [pasteResult.errors.slice(0, 10).map((err, i) => _jsx("div", { children: err }, i)), pasteResult.errors.length > 10 && _jsxs("div", { children: ["\u2026 and ", pasteResult.errors.length - 10, " more."] })] }))] })), _jsxs("div", { className: "flex flex-col sm:flex-row justify-center gap-3 sm:gap-4 pt-2", children: [_jsx("button", { onClick: handleClose, className: "flex items-center justify-center gap-2 px-6 py-3 rounded-lg border-2 font-medium transition-colors", style: { borderColor: 'var(--pewter)', color: 'var(--pewter)', backgroundColor: 'transparent' }, onMouseEnter: (e) => { e.currentTarget.style.backgroundColor = 'var(--pewter)'; e.currentTarget.style.color = 'var(--sand)'; }, onMouseLeave: (e) => { e.currentTarget.style.backgroundColor = 'transparent'; e.currentTarget.style.color = 'var(--pewter)'; }, children: "Cancel" }), _jsx("button", { onClick: onConfirm, disabled: mode === 'paste'
                                            ? pasteResult.coords.length === 0
                                            : isParsing || !featureCount || featureCount === 0, className: "flex items-center justify-center gap-2 px-6 py-3 rounded-lg font-medium transition-colors hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed", style: { backgroundColor: 'var(--forest)', color: 'var(--sand)' }, children: mode === 'paste' ? 'Add' : 'Import' })] })] })] })] }));
};
